import { Filter, FilterOption } from 'app/modules/filters/models';
import { AiAgentStatus } from 'app/modules/aiAgents/models';
import { FILTER_OPERATOR, FILTER_TYPE } from 'app/modules/filters/constants';
import { LocalStorageKeys } from 'app/shared/constants/localStorage';
import { TaskCategory } from 'app/modules/aiAgents/aiAgentTaskCategories';
import { AiAgentType } from 'app/modules/aiAgents/responses';

import { getValidFilters } from 'app/modules/filters/utils';
import { getLocalStorageJSON } from 'app/shared/utils/localStorage';

export function getAiAgentsFiltersKey(
  agentType?: AiAgentType,
): LocalStorageKeys {
  switch (agentType) {
    case 'case':
      return LocalStorageKeys.CASE_AI_AGENTS_FILTERS;
    case 'filing':
      return LocalStorageKeys.FILING_AI_AGENTS_FILTERS;
    default:
      return LocalStorageKeys.ALERT_AI_AGENTS_FILTERS;
  }
}

export const AI_AGENT_FILTER_KEYS = {
  STATUS: 'status',
} as const;

export const AI_AGENT_TASK_FILTER_KEYS = {
  CATEGORY: 'category',
  STATUS: 'task_status',
  TITLE: 'task_title',
} as const;

export const ALL_AI_AGENT_FILTER_OPTIONS: FilterOption[] = [
  {
    key: AI_AGENT_FILTER_KEYS.STATUS,
    label: 'Status',
    type: FILTER_TYPE.MULTI_SELECT,
    customize: {
      [FILTER_OPERATOR.IS_ONE_OF]: {
        inputProps: {
          options: [
            { value: AiAgentStatus.ACTIVE, text: 'Active' },
            { value: AiAgentStatus.ARCHIVED, text: 'Archived' },
          ] satisfies { text: string; value: AiAgentStatus }[],
        },
      },
      [FILTER_OPERATOR.IS_NOT_ONE_OF]: { disabled: true },
    },
  },
];

export const DEFAULT_AI_AGENT_FILTERS: Filter[] = [
  {
    key: AI_AGENT_FILTER_KEYS.STATUS,
    operator: FILTER_OPERATOR.IS_ONE_OF,
    value: [AiAgentStatus.ACTIVE],
  },
];

export const getDefaultAiAgentFilters = (agentType?: AiAgentType): Filter[] => {
  const filters = getValidFilters(
    getLocalStorageJSON(getAiAgentsFiltersKey(agentType)),
    ALL_AI_AGENT_FILTER_OPTIONS,
  );
  return [
    ...DEFAULT_AI_AGENT_FILTERS,
    ...filters.filter((i) => i.key !== AI_AGENT_FILTER_KEYS.STATUS),
  ];
};

export const getCategoryFilterOptions = (
  options: {
    value: TaskCategory;
    text: string;
  }[],
): FilterOption => {
  return {
    key: AI_AGENT_TASK_FILTER_KEYS.CATEGORY,
    label: 'Category',
    type: FILTER_TYPE.MULTI_SELECT,
    customize: {
      [FILTER_OPERATOR.IS_ONE_OF]: {
        inputProps: { options },
      },
      [FILTER_OPERATOR.IS_NOT_ONE_OF]: { disabled: true },
    },
  };
};

// AI Agent Tasks Filters
export const ALL_AI_AGENT_TASK_FILTER_OPTIONS: FilterOption[] = [
  // Initial ones, the rest will get populated dynamically
  getCategoryFilterOptions([
    {
      value: TaskCategory.FLAGGED_ACTIVITY_SIGNAL_AGENT,
      text: 'Flagged Activity',
    },
    {
      value: TaskCategory.ACCOUNT_RISK_SIGNAL_AGENT,
      text: 'Account Risk',
    },
  ]),
  {
    key: AI_AGENT_TASK_FILTER_KEYS.STATUS,
    label: 'Status',
    type: FILTER_TYPE.MULTI_SELECT,
    customize: {
      [FILTER_OPERATOR.IS_ONE_OF]: {
        inputProps: {
          options: [
            { value: 'active', text: 'Active' },
            { value: 'inactive', text: 'Inactive' },
          ],
        },
      },
      [FILTER_OPERATOR.IS_NOT_ONE_OF]: { disabled: true },
    },
  },
  {
    key: AI_AGENT_TASK_FILTER_KEYS.TITLE,
    label: 'Name',
    type: FILTER_TYPE.TEXT,
    customize: {
      [FILTER_OPERATOR.CONTAINS_TEXT]: {},
    },
  },
];

export const DEFAULT_AI_AGENT_TASK_FILTERS: Filter[] = [];
